import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext();

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    // Hydrate user from cache so a cold reload (especially while offline)
    // can render protected routes before the /auth/me network call resolves.
    try {
      const raw = localStorage.getItem('user');
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  useEffect(() => {
    if (token) {
      loadUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const loadUser = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
      try {
        localStorage.setItem('user', JSON.stringify(response.data));
      } catch {
        // ignore quota errors
      }
    } catch (error) {
      // Only log out on auth failures (401/403). For network errors
      // (offline, DNS, 5xx) keep the cached user so Field View can still
      // render the SPA shell + cached jobs while offline.
      const status = error?.response?.status;
      if (status === 401 || status === 403) {
        logout();
      } else {
        // Network / server error — keep the cached user we hydrated from
        // localStorage on init. Do nothing.
        console.warn('auth/me unreachable, keeping cached user:', error?.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const login = async (emailOrToken, password, turnstileToken) => {
    // If password is provided, do email/password login
    // Otherwise, assume it's a token-based login (from signup)
    if (password) {
      const headers = {};
      if (turnstileToken) headers['X-Turnstile-Token'] = turnstileToken;
      const response = await axios.post(`${API_URL}/api/auth/login`, { email: emailOrToken, password }, { headers });
      const { token: newToken } = response.data;
      localStorage.setItem('token', newToken);
      setToken(newToken);
      
      // Load user immediately after login
      try {
        const userResponse = await axios.get(`${API_URL}/api/auth/me`, {
          headers: { Authorization: `Bearer ${newToken}` }
        });
        setUser(userResponse.data);
        try { localStorage.setItem('user', JSON.stringify(userResponse.data)); } catch {}
      } catch (error) {
        console.error('Error loading user after login:', error);
      }
      
      return response.data;
    } else {
      // Token-based login (from signup)
      const newToken = emailOrToken;
      localStorage.setItem('token', newToken);
      setToken(newToken);
      
      // Load user with the new token
      try {
        const userResponse = await axios.get(`${API_URL}/api/auth/me`, {
          headers: { Authorization: `Bearer ${newToken}` }
        });
        setUser(userResponse.data);
        try { localStorage.setItem('user', JSON.stringify(userResponse.data)); } catch {}
      } catch (error) {
        console.error('Error loading user after token login:', error);
      }
      
      return { token: newToken };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setToken(null);
    setUser(null);
  };

  // RW-BUILD-004 — apply a new workspace-scoped token and route to its home.
  // A full navigation re-inits the app cleanly under the new workspace.
  const applyWorkspaceToken = (newToken, workspace) => {
    localStorage.setItem('token', newToken);
    localStorage.removeItem('user');
    const home = workspace === 'owner' ? '/client/dashboard' : '/dashboard';
    window.location.assign(home);
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, loading, token, applyWorkspaceToken }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}